/**
 * Snapshots — the per-player slice of the world that goes down the wire.
 *
 * The projection lives here, in the protocol package, rather than in
 * `apps/server`. Two reasons:
 *
 * - It is the definition of the wire format, expressed as code instead of as
 *   prose. A `Snapshot` type without the function that produces one is a
 *   description somebody has to re-implement correctly.
 * - It is pure, so area-of-interest culling — the thing that must not leak
 *   information — is testable without standing up a socket. "No entity data
 *   for actors outside the viewport margin" (M0-04) is an assertion here, not
 *   a packet capture somebody remembers to take.
 *
 * The arrow points protocol → sim, never back: this reads world state and
 * knows nothing about how it got that way.
 */
import { bandAt, bloodOf, bloodStage, canSee, carcassStage, carriedRadius, burnedDown, fireAt, chebyshev, hungerBand, offeredAffordances, signsNear, spotRange, isResting, isWellRested, restQualityAt, combatDice, DAY_SIGHT, daylight, EMPTY_CATALOGUE, freshnessOf, gameTime, hearthFacingAt, HEARTH_RADIUS, isRemedy, knows, learnedNote, lightsOf, nameFor, radiusOf, sightRadiusOf, standingOf, weatherOf, wetnessOf, HEAVY_BLEED, inBounds, infectionStageOf, hasLineOfSight, isBurning, isEmbers, familiarityProgress, hasTag, isHelpless, isLodged, kitOf, lootable, marksOf, mobilityAid, palled, partFactor, speedOf, staminaFraction, stackKey, tileIdAt, tileAt, SAY_RANGE, toolTagFor, objectiveProgress, renderErrandPrompt, withinEarshot, } from '@annwn/sim';
/**
 * The grid the client draws. Tuned to a 16×16-pixel cell on a 1080p screen
 * with room for the DOM overlay panels; the client reflows to its actual
 * window, and the server's area of interest is what is authoritative.
 */
export const VIEWPORT = { width: 48, height: 32 };
/**
 * Extra tiles culled *outside* the viewport, so an actor stepping into view
 * is already known to the client and glides in rather than popping. Eight
 * tiles is a second and a half of walking at two ticks a tile — comfortably
 * more than the interpolation buffer needs.
 */
export const AOI_MARGIN = 8;
export const AOI_SIZE = {
    width: VIEWPORT.width + AOI_MARGIN * 2,
    height: VIEWPORT.height + AOI_MARGIN * 2,
};
/**
 * Needs to assume before the server has said: fed, on their feet, on dry
 * ground. The counterpart to `DEFAULT_SKY`, and here for the same reason — the
 * tests that build snapshots by hand and the client that renders them should
 * agree about what "nothing known yet" looks like.
 */
export const DEFAULT_NEEDS = {
    hunger: 'fed',
    resting: false,
    rest: 'dry',
    fire: false,
    wellRested: false,
};
/**
 * The sky to assume before the server has said: broad noon, clear, with sight
 * enough to cover the whole area of interest.
 *
 * Optimistic deliberately. The other plausible default is midnight, and a
 * client that painted one frame of pitch dark before the first snapshot
 * arrived would flash black on every connection. It lives here rather than in
 * the client so that the tests which build snapshots by hand and the client
 * that renders them agree about what "no sky yet" looks like.
 */
export const DEFAULT_SKY = {
    hour: 12,
    minute: 0,
    daylight: 1,
    weather: 'clear',
    wind: 'sw',
    wetness: 0,
    sight: DAY_SIGHT,
    lit: false,
};
export const EMPTY_MEMORY = { tiles: null, actors: [] };
function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
export function rectContains(rect, x, y) {
    return x >= rect.x && y >= rect.y && x < rect.x + rect.width && y < rect.y + rect.height;
}
function intersect(a, b) {
    const x = Math.max(a.x, b.x);
    const y = Math.max(a.y, b.y);
    const right = Math.min(a.x + a.width, b.x + b.width);
    const bottom = Math.min(a.y + a.height, b.y + b.height);
    if (right <= x || bottom <= y)
        return null;
    return { x, y, width: right - x, height: bottom - y };
}
/**
 * The parts of `next` not already covered by `previous`.
 *
 * Walking east by one tile should cost one column of tiles, not a whole
 * viewport. Splitting into at most four strips — above, below, left, right of
 * the overlap — is what turns a 64×48 re-send into a 1×48 one.
 */
export function rectDifference(next, previous) {
    if (next.width <= 0 || next.height <= 0)
        return [];
    if (!previous)
        return [next];
    const overlap = intersect(next, previous);
    if (!overlap)
        return [next];
    const parts = [];
    const nextRight = next.x + next.width;
    const nextBottom = next.y + next.height;
    const overlapRight = overlap.x + overlap.width;
    const overlapBottom = overlap.y + overlap.height;
    if (overlap.y > next.y) {
        parts.push({ x: next.x, y: next.y, width: next.width, height: overlap.y - next.y });
    }
    if (overlapBottom < nextBottom) {
        parts.push({
            x: next.x,
            y: overlapBottom,
            width: next.width,
            height: nextBottom - overlapBottom,
        });
    }
    if (overlap.x > next.x) {
        parts.push({ x: next.x, y: overlap.y, width: overlap.x - next.x, height: overlap.height });
    }
    if (overlapRight < nextRight) {
        parts.push({
            x: overlapRight,
            y: overlap.y,
            width: nextRight - overlapRight,
            height: overlap.height,
        });
    }
    return parts;
}
/**
 * The area of interest around a point, clamped so it never runs off the map.
 * Clamping rather than truncating keeps the client's view full at the valley
 * edge instead of showing it a band of nothing.
 */
export function aoiRect(map, cx, cy, size = AOI_SIZE) {
    const width = Math.min(size.width, map.width);
    const height = Math.min(size.height, map.height);
    const x = clamp(cx - Math.floor(width / 2), 0, Math.max(0, map.width - width));
    const y = clamp(cy - Math.floor(height / 2), 0, Math.max(0, map.height - height));
    return { x, y, width, height };
}
/** Reads a rectangle of terrain out of the map, row-major. */
export function tilePatch(map, rect) {
    const tiles = new Array(rect.width * rect.height);
    for (let row = 0; row < rect.height; row += 1) {
        for (let column = 0; column < rect.width; column += 1) {
            tiles[row * rect.width + column] = tileIdAt(map, rect.x + column, rect.y + row);
        }
    }
    return { ...rect, tiles };
}
/**
 * Needs the catalogue and the tick that `toSelfCombat` needed all along, and
 * for the same reason: `down` is derived from the body plan world state only
 * names by id. Without content there is no plan to derive it from, so a
 * bodyless world reports everyone upright — which is correct, because in a
 * world with no bodies nobody can be knocked out of one.
 */
function toCombat(combatant, catalogue, tick) {
    if (!combatant)
        return null;
    return {
        guarding: combatant.guarding,
        sneaking: combatant.sneaking ?? false,
        prone: combatant.vitals.prone,
        down: isHelpless(catalogue, combatant, tick),
        strikeAt: combatant.swing?.strikeAt ?? 0,
        recoveryUntil: combatant.recoveryUntil,
        channel: toChannel(combatant.channel),
    };
}
function toChannel(channel) {
    if (!channel)
        return null;
    return {
        verb: channel.verb,
        targetId: channel.targetId,
        startedAt: channel.startedAt,
        completesAt: channel.completesAt,
    };
}
/**
 * The viewer's own condition. Needs the catalogue, because dice and stamina are
 * derived from the body plan and the kit that world state only names by id —
 * and without content there is nothing to derive them from, so the block is
 * simply absent.
 */
function toSelfCombat(combatant, catalogue, tick, medicine, aid) {
    const base = toCombat(combatant, catalogue, tick);
    if (!combatant || !base)
        return null;
    const kit = kitOf(catalogue, combatant);
    if (!kit)
        return null;
    const blood = bloodOf(combatant.vitals);
    const open = combatant.vitals.wounds.filter((wound) => wound.state !== 'healed');
    const healed = combatant.vitals.wounds.filter((wound) => wound.state === 'healed');
    const reveal = medicine >= REVEAL_MEDICINE;
    return {
        ...base,
        engagedWith: combatant.engagedWith,
        weapon: {
            id: kit.weapon.id,
            name: kit.weapon.name,
            range: kit.weapon.ranged?.range ?? null,
            arrows: kit.weapon.ranged ? (combatant.loadout.ammunition ?? 0) : null,
            stowable: kit.weapon.id !== catalogue.defaultLoadout?.weaponId &&
                Boolean(catalogue.item?.(kit.weapon.id)),
        },
        stamina: staminaFraction(combatant, kit),
        band: bandAt(staminaFraction(combatant, kit)).id,
        staggered: combatant.vitals.staggered,
        dice: combatDice(combatant, kit),
        // `aid` is what the crutch in the satchel is worth (M4-05). Threaded in
        // rather than read here, because a snapshot builder that reached into the
        // items table would be a second place that decides what a crutch does.
        speed: speedOf(kit.plan, combatant, aid),
        blood,
        bloodStage: bloodStage(blood),
        marks: marksOf(catalogue, combatant.vitals).map((mark) => ({
            name: mark.name,
            examine: mark.examine,
        })),
        burden: combatant.burden
            ? { targetId: combatant.burden.targetId, mode: combatant.burden.mode }
            : null,
        parts: kit.plan.parts.map((part) => toPart(part, open, combatant)),
        wounds: [...open]
            .sort((left, right) => right.level - left.level || left.id.localeCompare(right.id))
            .map((wound) => toWound(wound, reveal)),
        // Newest first: the scar somebody wants to read about is the one they got
        // last week, not the one they arrived in the valley with.
        scars: [...healed]
            .sort((left, right) => right.healedAt - left.healedAt || left.id.localeCompare(right.id))
            .map((wound) => toWound(wound, reveal)),
        relieved: combatant.vitals.relief > 0,
    };
}
/**
 * Medicine familiarity at which a wound's true infection stage becomes legible
 * (M4-02). Below it, a fevered wound and a gangrenous one both read `inflamed` —
 * angry and hot, but no telling how deep it has gone.
 */
const REVEAL_MEDICINE = 15;
/** The infection stage an examiner reads — the truth only with the skill for it. */
function revealedInfection(wound, reveal) {
    const stage = infectionStageOf(wound);
    if (stage === null)
        return null;
    return reveal ? stage : 'inflamed';
}
/** One wound, with everything the diagram needs to talk about it in prose. */
function toWound(wound, reveal) {
    return {
        id: wound.id,
        part: wound.part,
        zone: wound.zone,
        level: wound.level,
        state: wound.state,
        bleeding: wound.bleedRate > 0,
        // A word, never the rate: the UI has nowhere to show a number and should
        // not be tempted to grow one.
        bleedStage: wound.bleedRate <= 0 ? 'closed' : wound.bleedRate >= HEAVY_BLEED ? 'heavy' : 'open',
        type: wound.type,
        inflictedAt: wound.inflictedAt,
        healedAt: wound.healedAt,
        effects: [...wound.effects],
        dirty: wound.dirty,
        sutured: wound.sutured,
        cauterized: wound.cauterized,
        lodged: reveal && isLodged(wound),
        infection: revealedInfection(wound, reveal),
        cause: wound.provenance.cause,
        by: wound.provenance.attacker?.name ?? null,
        weapon: wound.provenance.weapon ?? null,
        where: wound.provenance.where,
    };
}
/**
 * One part, coloured by the worst thing that has happened to it.
 *
 * The worst *unhealed* wound rather than a sum: a forearm with three scratches
 * is not a ruined forearm, and a diagram that added them up would say it was.
 */
function toPart(part, wounds, combatant) {
    let level = 0;
    let bleeding = false;
    for (const wound of wounds) {
        if (wound.part !== part.id)
            continue;
        if (wound.level > level)
            level = wound.level;
        if (wound.bleedRate > 0)
            bleeding = true;
    }
    const state = combatant.vitals.body.parts[part.id];
    return {
        id: part.id,
        name: part.name,
        zone: part.zone,
        side: part.side,
        vital: part.vital ?? false,
        level,
        bleeding,
        function: state ? partFactor(state) : 1,
    };
}
function toEntity(actor, combatant, catalogue, tick, defId) {
    return {
        id: actor.id,
        kind: actor.kind,
        name: actor.name,
        x: actor.x,
        y: actor.y,
        facing: actor.facing,
        combat: toCombat(combatant, catalogue, tick),
        // Only when it is true, so the field costs nothing on the wire for the
        // majority of the valley who have never died.
        ...(combatant && palled(catalogue, combatant.vitals) ? { pale: true } : {}),
        ...(defId === undefined ? {} : { defId }),
    };
}
/** A fire, as anyone walking past sees it. */
function toFire(light, tick) {
    return {
        id: light.id,
        x: light.x,
        y: light.y,
        radius: radiusOf(light, tick),
        burned: burnedDown(light, tick),
        embers: isEmbers(light, tick),
    };
}
/** A body on the ground, as anyone walking past sees it (M2-07). */
function toCorpse(corpse) {
    return {
        id: corpse.id,
        ownerId: corpse.ownerId,
        name: corpse.name,
        x: corpse.x,
        y: corpse.y,
        diedAt: corpse.diedAt,
        carries: lootable(corpse.loadout),
    };
}
/** A grave, as somebody standing over it reads it (M4-07). */
function toGrave(grave) {
    return {
        id: grave.id,
        x: grave.x,
        y: grave.y,
        name: grave.name,
        epitaph: grave.epitaph,
        buriedBy: grave.buriedBy,
        robbed: grave.robbed,
    };
}
/**
 * A dead animal, as a passer-by sees it (M3-06).
 */
function toCarcass(carcass, tick) {
    return {
        id: carcass.id,
        name: carcass.name,
        x: carcass.x,
        y: carcass.y,
        speciesId: carcass.speciesId,
        stage: carcassStage(carcass, tick),
    };
}
/**
 * A patch of plants, as the viewer sees it (M3-03).
 *
 * `null` when the species is not in the catalogue — a world running content
 * that has no flora in it should show empty ground rather than take the
 * snapshot down, the same call `toEntity` makes about a missing body plan.
 */
function toFlora(patch, catalogue, lore) {
    const def = catalogue.flora?.(patch.speciesId);
    if (!def)
        return null;
    const item = catalogue.item?.(def.itemId);
    // Known plants are named; unknown ones are described. The species id still
    // travels, because the client needs something stable to pick a glyph by —
    // and a glyph is not a name.
    const name = item && knows(lore, item.id) ? def.name : (item?.unknownName ?? def.name);
    return {
        x: patch.x,
        y: patch.y,
        speciesId: patch.speciesId,
        name,
        standing: standingOf(patch, def),
    };
}
/**
 * The viewer's own satchel (M3-03).
 *
 * Freshness and weight are computed here rather than on the client for the
 * reason every other derived number is: the shelf life lives in the catalogue,
 * and a client doing this arithmetic would need the catalogue on the wire.
 */
function toSatchel(inventory, catalogue, lore, tick) {
    if (!inventory || inventory.items.length === 0)
        return { items: [], weight: 0 };
    const items = [];
    let weight = 0;
    for (const [index, stack] of inventory.items.entries()) {
        const def = catalogue.item?.(stack.defId);
        if (!def)
            continue;
        const rowWeight = def.weight * stack.count;
        weight += rowWeight;
        items.push({
            key: stackKey(stack, index),
            defId: def.id,
            name: nameFor(def, lore),
            count: stack.count,
            weight: rowWeight,
            freshness: def.shelfLife === null ? null : freshnessOf(def, stack, tick),
            edible: def.tags.includes('food') && (def.nutrition ?? 0) > 0,
            raw: def.tags.includes('raw'),
            equippable: catalogue.weapon(def.id) !== null,
            note: learnedNote(catalogue.herb?.(def.id) ?? null, lore),
            remedy: isRemedy(def),
            provenance: stack.note ?? null,
        });
    }
    // By name, so the pane does not reorder itself as stacks are spent — a list
    // that shuffles while you read it is a list nobody reads.
    items.sort((left, right) => (left.name < right.name ? -1 : left.name > right.name ? 1 : 0));
    return { items, weight };
}
/**
 * The five creation attributes, in the order the sheet lists them (matching the
 * approved reference). Kept here rather than derived from `Attributes`' key order
 * so the display order is a decision, not an accident of the interface.
 */
const SHEET_ATTRIBUTES = [
    ['frame', 'Frame'],
    ['toughness', 'Toughness'],
    ['agility', 'Agility'],
    ['wit', 'Wit'],
    ['presence', 'Presence'],
];
/**
 * The character sheet block (RC2-03) — identity, attributes, familiarities and
 * errands, assembled server-side so the client surface stays presentation-only.
 *
 * The paper-doll and inventory are deliberately absent: they read off `combat`
 * and `satchel`, which self already carries, and duplicating them here would be
 * two copies of one truth. Familiarity progress is worded here (`phrase`, a bar
 * `fill`), never sent as reps — the "no number you do not mean" rule holds on
 * the wire. The door predicate is RC2-04's: a familiarity that needs a tool
 * (woodcraft needs an axe) opens the moment the viewer carries something tagged
 * for it, so the rung flips from "needs an axe" to a rank word on the tick the
 * axe goes into the satchel. `toolTagFor` reads the one door table `chop` reads,
 * so the rule the sheet shows and the rule the verb enforces cannot drift.
 */
function toSheet(world, viewer, combatant, catalogue, lore) {
    const loadout = combatant?.loadout;
    const brief = loadout?.background ? (catalogue.background?.(loadout.background) ?? null) : null;
    const identity = {
        name: viewer.name,
        trade: brief?.trade ?? null,
        blurb: brief?.blurb ?? null,
    };
    const attrs = loadout?.attributes;
    const attributes = attrs
        ? SHEET_ATTRIBUTES.map(([key, label]) => ({ key, label, value: attrs[key] }))
        : [];
    // A door opens when the viewer carries the tool it names. `hasTag` reads the
    // satchel through the catalogue, exactly as the `chop` verb's own fault does.
    const opened = (familiarity) => {
        const tool = toolTagFor(familiarity);
        return tool === undefined || hasTag(world, catalogue, viewer.id, tool);
    };
    const familiarities = familiarityProgress(lore, opened).map((rung) => ({
        familiarity: rung.familiarity,
        label: rung.label,
        phrase: rung.phrase,
        band: rung.band,
        fill: rung.fill,
        locked: rung.locked,
    }));
    const active = world.errands?.[viewer.id]?.active ?? [];
    const errands = active.map((one) => {
        const def = catalogue.errand?.(one.defId) ?? null;
        const giver = def ? (catalogue.villager?.(def.giver)?.name ?? def.giver) : '';
        return {
            id: one.defId,
            prompt: def ? renderErrandPrompt(world, catalogue, def, viewer) : one.defId,
            giver,
            acceptedTick: one.acceptedTick,
            objectives: def ? objectiveProgress(def, one) : [],
        };
    });
    return { identity, attributes, familiarities, errands };
}
/**
 * Project the world down to what one player may see.
 *
 * Returns the updated viewer memory alongside the snapshot rather than
 * mutating it, so a caller can build a snapshot speculatively — and so the
 * culling logic has no hidden state for a test to have to set up.
 */
export function buildSnapshot(world, viewerId, memory = EMPTY_MEMORY, options = {}) {
    const ack = options.ack ?? 0;
    const viewer = world.actors[viewerId];
    // Not seated yet — the join intent is queued but has not been simulated.
    // Send a well-formed empty snapshot rather than nothing, so the client has
    // one code path instead of two.
    if (!viewer) {
        const snapshot = {
            tick: world.tick,
            self: null,
            actors: [],
            corpses: [],
            graves: [],
            carcasses: [],
            fires: [],
            flora: [],
            tiles: [],
            signs: [],
            gone: memory.actors,
            ack,
            sky: toSky(world, viewerId, options.catalogue ?? EMPTY_CATALOGUE),
        };
        return { snapshot, memory: { tiles: memory.tiles, actors: [] } };
    }
    const rect = aoiRect(world.map, viewer.x, viewer.y, options.aoi ?? AOI_SIZE);
    const sky = toSky(world, viewerId, options.catalogue ?? EMPTY_CATALOGUE);
    /**
     * The M3-05 cull: inside the area of interest *and* actually visible.
     *
     * Two filters rather than one, because they answer different questions. The
     * rect is about the wire — it bounds how much can ever be sent — and sight is
     * about the world: at midnight without a light it collapses to three tiles,
     * and a wolf four tiles away is not in the packet at all. That is the
     * acceptance criterion, and it is enforced here rather than in the renderer
     * precisely so that no client can turn it off.
     */
    const inSight = (x, y) => rectContains(rect, x, y) && canSee(world, viewer, x, y, sky.sight);
    const visible = Object.values(world.actors)
        .filter((actor) => inSight(actor.x, actor.y))
        .sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
    const visibleIds = new Set(visible.map((actor) => actor.id));
    const gone = memory.actors.filter((id) => !visibleIds.has(id));
    const tiles = rectDifference(rect, memory.tiles).map((part) => tilePatch(world.map, part));
    const catalogue = options.catalogue ?? EMPTY_CATALOGUE;
    const corpses = Object.values(world.corpses)
        .filter((corpse) => inSight(corpse.x, corpse.y))
        .sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0))
        .map(toCorpse);
    const graves = Object.values(world.graves)
        .filter((grave) => inSight(grave.x, grave.y))
        .sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0))
        .map(toGrave);
    const carcasses = Object.values(world.carcasses)
        .filter((carcass) => inSight(carcass.x, carcass.y))
        .sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0))
        .map((carcass) => toCarcass(carcass, world.tick));
    /**
     * A fire is visible as far as you can *see* it, not as far as you can see
     * by it.
     *
     * Deliberately not `inSight`. Sight radius is built from ambient light and
     * whatever is in your own hand (`sightRadiusOf`), and a fire on the ground
     * contributes nothing to it — so culling fires by sight meant that at night,
     * four tiles from your own campfire, it stopped existing. Which is the
     * opposite of what a fire is: the brightest thing in a dark valley and the
     * one landmark you can walk back to.
     *
     * So the rule is line of sight and the area of interest, and nothing else. A
     * fire behind a hut wall is hidden; a fire across the dark green is not. It
     * leaks nothing — what is *standing* at it still goes through `inSight` like
     * every other actor, so a watch fire tells you there is a camp there and not
     * who is sitting at it.
     */
    const fireInSight = (x, y) => rectContains(rect, x, y) && hasLineOfSight(world.map, viewer.x, viewer.y, x, y);
    const fires = Object.values(lightsOf(world))
        // On the ground and still going. A light in somebody's hand is theirs and
        // rides on `sky.lit` instead of being drawn twice.
        .filter((light) => light.kind === 'fire' &&
        light.carriedBy === null &&
        isBurning(light, world.tick) &&
        fireInSight(light.x, light.y))
        .sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0))
        .map((light) => toFire(light, world.tick));
    // Hearths predate light sources and live in the terrain layer, so derive
    // their permanent light while projecting the existing map. This also makes
    // old saves light up immediately, without rewriting persisted world state.
    for (let y = rect.y; y < rect.y + rect.height; y += 1) {
        for (let x = rect.x; x < rect.x + rect.width; x += 1) {
            if (tileAt(world.map, x, y) !== 'hearth' || !fireInSight(x, y))
                continue;
            fires.push({
                id: `hearth:${x},${y}`,
                kind: 'hearth',
                x,
                y,
                radius: HEARTH_RADIUS,
                burned: 0,
                embers: false,
                facing: hearthFacingAt(world.map, x, y),
            });
        }
    }
    fires.sort((left, right) => (left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
    const lore = world.lore[viewerId];
    const flora = Object.values(world.flora)
        .filter((patch) => inSight(patch.x, patch.y))
        // Row-major, so the list is stable and diffs read the way the map does.
        .sort((left, right) => (left.y !== right.y ? left.y - right.y : left.x - right.x))
        .map((patch) => toFlora(patch, catalogue, lore))
        .filter((patch) => patch !== null);
    const snapshot = {
        tick: world.tick,
        self: {
            ...toEntity(viewer, world.combat[viewerId], catalogue, world.tick),
            moveReadyAt: viewer.moveReadyAt,
            combat: toSelfCombat(world.combat[viewerId], catalogue, world.tick, lore?.medicine ?? 0, mobilityAid(world.inventories[viewerId], (id) => catalogue.item?.(id) ?? null)),
            satchel: toSatchel(world.inventories[viewerId], catalogue, lore, world.tick),
            needs: toNeeds(world, viewer, world.needs?.[viewerId]),
            affordances: toAffordances(world, catalogue, viewer, adjacentBody(world, catalogue, viewer)),
            sheet: toSheet(world, viewer, world.combat[viewerId], catalogue, lore),
        },
        actors: visible.map((actor) => toEntity(actor, world.combat[actor.id], catalogue, world.tick, world.villagers[actor.id]?.defId)),
        corpses,
        graves,
        carcasses,
        fires,
        flora,
        tiles,
        gone,
        ack,
        sky,
        signs: visibleSigns(world, viewer, world.lore[viewerId]?.tracking ?? 0),
    };
    return { snapshot, memory: { tiles: rect, actors: [...visibleIds] } };
}
/**
 * Marks close enough for this viewer to notice (M3-08).
 *
 * Ordered by position, like flora, so the list is stable and diffs read the way
 * the map does.
 */
function visibleSigns(world, viewer, familiarity) {
    const range = spotRange(familiarity);
    const near = [...signsNear(world.signs, viewer.x, viewer.y, range)];
    return near
        .sort((left, right) => (left.y !== right.y ? left.y - right.y : left.x - right.x))
        .map((sign) => ({
        x: sign.x,
        y: sign.y,
        kind: sign.kind,
        strength: Math.round(Math.max(0, Math.min(1, sign.strength)) * 100) / 100,
    }));
}
/**
 * What the viewer can do where they stand (M3-04).
 *
 * A straight map of `offeredAffordances` onto the wire shape. There is no
 * decision in here on purpose — the sim decided, and a protocol that re-judged
 * anything would be the second opinion this ticket exists to delete.
 */
function toAffordances(world, catalogue, viewer, targetId) {
    return offeredAffordances(world, catalogue, viewer, world.tick, targetId).map((offer) => ({
        verb: offer.verb,
        label: offer.label,
        target: offer.target,
        available: offer.available,
        reason: offer.reason,
        note: offer.note,
        channelTicks: offer.channelTicks,
    }));
}
/**
 * The helpless body at the viewer's elbow, if there is one.
 *
 * Only helpless ones, and only adjacent ones, because those are the only bodies
 * the four `body` verbs apply to — `helplessFault` says so, and offering "rob"
 * against a man on his feet would be a greyed-out row on every menu in the
 * game. The nearest by id order, so the choice is stable between ticks.
 */
function adjacentBody(world, catalogue, viewer) {
    for (const id of Object.keys(world.actors).sort()) {
        if (id === viewer.id)
            continue;
        const actor = world.actors[id];
        const combatant = world.combat[id];
        if (!actor || !combatant)
            continue;
        if (chebyshev(viewer.x, viewer.y, actor.x, actor.y) > 1)
            continue;
        if (!isHelpless(catalogue, combatant, world.tick))
            continue;
        return id;
    }
    return undefined;
}
/**
 * Hunger, rest and what is burning nearby (M3-06).
 *
 * `rest` is computed from where the viewer is *standing*, not from where they
 * last slept, because the question a player is actually asking is "is this a
 * place worth stopping in" — and the answer has to be available before they lie
 * down rather than after.
 */
function toNeeds(world, viewer, needs) {
    return {
        hunger: hungerBand(needs?.food ?? 1000),
        resting: needs ? isResting(needs) : false,
        rest: restQualityAt(world, viewer.x, viewer.y, world.tick),
        fire: fireAt(world, viewer.x, viewer.y, world.tick) !== null,
        wellRested: needs ? isWellRested(needs, world.tick) : false,
    };
}
/**
 * The sky block (M3-05).
 *
 * Terrain is deliberately *not* culled by sight, and only entities are. Two
 * reasons, and they are worth stating because the asymmetry looks like an
 * oversight: the ground does not move, so a tile sent once is map memory rather
 * than live information — the client already dims everything outside the area
 * of interest — and a player walking a dark valley learns its shape by walking
 * it, which is what a person would. What must never leak is *what is standing
 * on it*, and nothing standing on it survives `inSight` above.
 */
function toSky(world, viewerId, catalogue) {
    const weather = weatherOf(world);
    const time = gameTime(world.tick);
    return {
        hour: time.hour,
        minute: time.minute,
        daylight: daylight(time),
        weather: weather.kind,
        wind: weather.wind,
        wetness: wetnessOf(weather),
        sight: sightRadiusOf(world, catalogue, viewerId, world.tick),
        lit: carriedRadius(lightsOf(world), viewerId, world.tick) > 0,
    };
}
/**
 * Spatial say (design spec §8), re-exported beside the projection it belongs
 * next to: "who receives this" is the same question area-of-interest asks, and
 * a caller answering it should not have to reach past the protocol.
 *
 * The rule itself lives in `@annwn/sim`, where M0-06 put it — twelve tiles is
 * a fact about the world, and the server deciding it independently would be a
 * second rules engine nobody was maintaining.
 */
export { SAY_RANGE, withinEarshot };
/** Re-exported so callers can bounds-check without reaching past the protocol. */
export { inBounds };
//# sourceMappingURL=snapshot.js.map